import { Link } from 'react-router-dom';
import HERO_IMAGES from '../heroImages';
import useInView from '../hooks/useInView';
import SEO from '../components/SEO/SEO';
import { ArrowRight, Shield, Code2, Zap, User, Star, Phone } from 'lucide-react';
import reviews from '../data/reviews.json';
import './HomePage.css';

const stats = [
  { value: '20+', label: 'Active Clients' },
  { value: '100+', label: 'Projects Delivered' },
  { value: '3+', label: 'Years Active' },
  { value: '100%', label: 'Client Satisfaction' },
];

const uniquePoints = [
  { icon: <Shield size={24} />, title: 'Cybersecurity First', desc: 'Every product ships hardened with security audits, penetration testing, and best-practice encryption.', color: '#0A4FFF', bg: 'https://images.unsplash.com/photo-1751738567808-6affa516fedc?fm=jpg&q=60&w=1200&auto=format&fit=crop' },
  { icon: <Code2 size={24} />, title: 'Secure Development', desc: 'OWASP-compliant code, dependency scanning, and security reviews baked into every sprint.', color: '#7C3AED', bg: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?fm=jpg&q=60&w=1200&auto=format&fit=crop' },
  { icon: <User size={24} />, title: 'Personalised Delivery', desc: 'No templates, no copy-paste. Every site and app reflects your brand identity, audience, and goals.', color: '#059669', bg: 'https://images.unsplash.com/photo-1600832331197-ad575931911b?fm=jpg&q=60&w=1200&auto=format&fit=crop' },
  { icon: <Zap size={24} />, title: 'Built-in Automation', desc: 'We engineer workflows that save time — automated reports, triggers, integrations — less repetitive work.', color: '#D97706', bg: 'https://images.unsplash.com/photo-1697577418970-95d99b5a55cf?fm=jpg&q=60&w=1200&auto=format&fit=crop' },
];

// Tech stack grouped by category
const techStack = [
  {
    category: 'Frontend',
    items: ['React', 'Next.js', 'TypeScript', 'Tailwind'],
  },
  {
    category: 'Backend',
    items: ['Node.js', 'Python', 'Express', 'FastAPI'],
  },
  {
    category: 'Database',
    items: ['PostgreSQL', 'MongoDB', 'Redis', 'Prisma'],
  },
  {
    category: 'Cloud & DevOps',
    items: ['AWS', 'Docker', 'Kubernetes', 'CI/CD'],
  },
  {
    category: 'Security',
    items: ['OWASP', 'Pentest', 'JWT', 'OAuth 2.0'],
  },
  {
    category: 'Tools',
    items: ['Git', 'Figma', 'Postman', 'Jira'],
  },
];

const workflowSteps = [
  { step: '01', title: 'Connect with Client', desc: 'Initial discovery call to understand your vision and goals.', type: 'start' },
  { step: '02', title: 'Scope Analysis', desc: 'Deep dive into requirements, tech stack, and timeline.', type: '' },
  { step: '03', title: 'Scope Document', desc: 'Detailed scope delivered to client for review and approval.', type: '' },
  { step: '04', title: 'Project Kickoff', desc: 'On client confirmation, development begins with a dedicated team.', type: '' },
  { step: '05', title: 'Development Sprint', desc: 'Agile development with bi-weekly demos and full transparency.', type: 'milestone' },
  { step: '06', title: 'Initial Testing', desc: 'Dev build handed to QA for functional testing and bug identification.', type: 'milestone' },
  { step: '07', title: 'Client Review', desc: 'Client reviews the tested build and submits change requests.', type: '' },
  { step: '08', title: 'Security Audit', desc: 'Comprehensive cybersecurity checks, penetration testing, compliance review.', type: 'milestone' },
  { step: '09', title: 'Final Delivery', desc: 'Source code, documentation, and full handover package delivered.', type: 'end' },
];

const phases = [
  { label: 'Discovery & Planning', color: '#0A4FFF', steps: [workflowSteps[0], workflowSteps[1], workflowSteps[2], workflowSteps[3]] },
  { label: 'Build & Test', color: '#7C3AED', steps: [workflowSteps[4], workflowSteps[5]] },
  { label: 'Review & Launch', color: '#059669', steps: [workflowSteps[6], workflowSteps[7], workflowSteps[8]] },
];

function FlowNode({ step, color }) {
  return (
    <div className="flow-node" style={{ '--flow-color': color }}>
      <div className="flow-node__badge">{step.step}</div>
      <div className="flow-node__title">{step.title}</div>
      <div className="flow-node__desc">{step.desc}</div>
    </div>
  );
}

function ArrowH() {
  return (
    <svg className="flow-arrow-h__svg" viewBox="0 0 100 12" preserveAspectRatio="none">
      <line x1="2" y1="6" x2="92" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <polyline points="86,2 94,6 86,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowV() {
  return (
    <svg className="flow-arrow-v__svg" viewBox="0 0 12 60" preserveAspectRatio="none">
      <line x1="6" y1="2" x2="6" y2="50" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <polyline points="2,44 6,52 10,44" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const allReviews = [...reviews, ...reviews];

export default function HomePage() {
  const [techStackRef, techStackInView] = useInView();
  const [uniqueRef, uniqueInView] = useInView();
  const [reviewsRef, reviewsInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  return (
    <main className="home">
      <SEO
        title="Secure Web & App Development Company in India"
        description="ExploitEye builds secure, scalable web apps, mobile apps, automation, digital marketing, and cybersecurity solutions (VAPT) for ambitious businesses across India. 100+ projects delivered."
        path="/"
        keywords="web development company India, app development agency, cybersecurity consulting, VAPT services, digital agency Chennai"
      />
      {/* ── HERO ── */}
      <section className="hero" style={{ backgroundImage: `url(${HERO_IMAGES.home})` }}>
        <div className="hero__bg-gradient" />
        <div
          className="container hero__inner"
          style={{ maxWidth: 'var(--container)', margin: '0 auto', padding: '80px 24px' }}
        >
          <div className="hero__content">
            <h1 className="hero__title">
              We Build Digital Products<br />
              <span className="gradient-text">That Outlast Trends.</span>
            </h1>
            <p className="hero__subtitle">
              ExploitEye engineers secure, scalable web applications and enterprise platforms — with cybersecurity baked in from day one.
            </p>
            <div className="hero__actions">
              <Link to="/contact" className="btn-primary">
                Start Your Project <ArrowRight size={16} />
              </Link>
              <Link to="/portfolio" className="btn-ghost">
                View Our Work
              </Link>
            </div>
            <div className="hero__trust">
              <div className="hero__trust-avatars">
                {['AM', 'PS', 'KR', 'MK', 'VN'].map((a, i) => (
                  <div key={i} className="hero__trust-avatar">{a}</div>
                ))}
              </div>
              <span className="hero__trust-text">Trusted by 20+ clients across India</span>
            </div>
          </div>

          {/* Tech Stack Visual */}
          <div
            className={`hero__visual reveal ${techStackInView ? 'reveal--visible' : ''}`}
            ref={techStackRef}
          >
            <div className="techstack-card">
              <div className="techstack-card__header">
                <div className="techstack-card__dots">
                  <span className="techstack-card__dot techstack-card__dot--r" />
                  <span className="techstack-card__dot techstack-card__dot--y" />
                  <span className="techstack-card__dot techstack-card__dot--g" />
                </div>
                <span className="techstack-card__title">Our Tech Stack</span>
              </div>
              <div className="techstack-card__body">
                {techStack.map((group, gi) => (
                  <div key={gi} className="techstack-group">
                    <div className="techstack-group__label">{group.category}</div>
                    <div className="techstack-group__items">
                      {group.items.map((item, ii) => (
                        <span key={ii} className="techstack-pill">{item}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="hero__stats">
          <div className="container">
            <div className="stats-track">
              {stats.map((s, i) => (
                <div key={i} className="stat-item">
                  <strong>{s.value}</strong>
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY UNIQUE ── */}
      <section className="unique-section">
        <div className="container">
          <div className="section-center">
            <span className="section-label">Why Choose Us</span>
            <h2 className="section-heading" style={{ textAlign: 'center' }}>
              What Makes ExploitEye<br />
              <span className="gradient-text">Genuinely Different</span>
            </h2>
            <p className="section-subheading" style={{ textAlign: 'center', margin: '16px auto 0' }}>
              We don't just build products — we build secure, automated, personalised platforms that grow with your business.
            </p>
          </div>
          <div className="unique-grid" ref={uniqueRef}>
            {uniquePoints.map((u, i) => (
              <div
                key={i}
                className={`unique-card reveal ${uniqueInView ? 'reveal--visible' : ''}`}
                style={{ '--card-color': u.color, '--card-bg': `url(${u.bg})`, '--reveal-delay': `${i * 0.1}s` }}
              >
                <div className="unique-card__icon">{u.icon}</div>
                <h3 className="unique-card__title">{u.title}</h3>
                <p className="unique-card__desc">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WORKFLOW FLOW DIAGRAM ── */}
      <section className="workflow-section">
        <div className="container">
          <div className="section-center">
            <span className="section-label">Our Process</span>
            <h2 className="section-heading" style={{ textAlign: 'center' }}>
              The ExploitEye<br />
              <span className="gradient-text">Workflow</span>
            </h2>
            <p className="section-subheading" style={{ textAlign: 'center', margin: '16px auto 64px' }}>
              A transparent, proven 9-step process — from your first call to final source code delivery.
            </p>
          </div>

          <div className="flow-diagram-v3">
            {phases.map((phase, pi) => (
              <div key={pi} className="flow-phase-v3">
                <div className="flow-phase-label">{phase.label}</div>

                <div className="flow-row-v3" style={{ '--cols': phase.steps.length }}>
                  {phase.steps.map((step, si) => (
                    <div key={si} className="flow-cell">
                      <FlowNode step={step} color={phase.color} />
                      {si < phase.steps.length - 1 && (
                        <div className="flow-arrow-h" aria-hidden="true">
                          <ArrowH />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {pi < phases.length - 1 && (
                  <div className="flow-arrow-v" aria-hidden="true">
                    <ArrowV />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REVIEWS ── */}
      <section className="reviews-section" ref={reviewsRef}>
        <div className="container">
          <div className={`section-center reveal ${reviewsInView ? 'reveal--visible' : ''}`}>
            <span className="section-label">Client Feedback</span>
            <h2 className="section-heading" style={{ textAlign: 'center' }}>
              Trusted by Entrepreneurs<br />
              <span className="gradient-text">Across India</span>
            </h2>
          </div>
        </div>
        <div
          className={`reviews-track-wrap reveal ${reviewsInView ? 'reveal--visible' : ''}`}
          style={{ marginTop: 56, '--reveal-delay': '0.15s' }}
        >
          <div className="reviews-track">
            {allReviews.map((r, idx) => (
              <div key={idx} className="review-card">
                <div className="review-card__stars">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} size={14} fill="#F59E0B" stroke="none" />
                  ))}
                </div>
                <p className="review-card__text">"{r.review}"</p>
                <div className="review-card__author">
                  <div className="review-card__avatar">{r.avatar}</div>
                  <div>
                    <strong>{r.name}</strong>
                    <span>{r.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={`cta-strip reveal ${ctaInView ? 'reveal--visible' : ''}`} ref={ctaRef}>
        <div className="cta-strip__orb cta-strip__orb--1" />
        <div className="cta-strip__orb cta-strip__orb--2" />
        <div className="container">
          <div className="cta-strip-inner">
            <div className="cta-strip-text">
              <h2>Ready to Build Something Exceptional?</h2>
              <p>Free consultation. No obligations. Just results.</p>
            </div>
            <div className="cta-strip-actions">
              <Link to="/contact" className="btn-primary">
                Start a Conversation <ArrowRight size={16} />
              </Link>
              <a href="tel:+916379197078" className="btn-ghost">
                <Phone size={15} /> Call Us Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}